<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-md-12">
        <div class="card shadow-lg">
          <div class="card-header pb-0">
            <div class="d-flex align-items-center">
              <p class="mb-0 text-uppercase font-weight-bold">Editar Sucursal / Agencia</p>
            </div>
          </div>
          <div class="card-body">
            <div v-if="isLoadingData" class="text-center py-5">
              <div class="spinner-border text-success" role="status"></div>
              <p class="mt-2 text-sm text-secondary">Cargando información de la sucursal...</p>
            </div>

            <form v-else @submit.prevent="updateBranch">
              <h6 class="text-uppercase text-body text-xs font-weight-bolder">Información General</h6>
              <div class="row">
                <div class="col-md-8">
                  <label for="name" class="form-control-label">Nombre de la Sucursal</label>
                  <argon-input
                    id="name"
                    type="text"
                    v-model="branch.name"
                  />
                  <small v-if="submitted && v$.branch.name.$error" class="text-danger">El nombre es requerido</small>
                </div>

                <div class="col-md-4">
                  <label for="phone" class="form-control-label">Teléfono de Contacto</label>
                  <argon-input
                    id="phone"
                    type="text"
                    v-model="branch.phone"
                  />
                </div>

                <div class="col-md-12 mt-3">
                  <label for="address" class="form-control-label">Dirección Física</label>
                  <argon-textarea
                    id="address"
                    v-model="branch.address"
                  />
                  <small v-if="submitted && v$.branch.address.$error" class="text-danger">La dirección es requerida</small>
                </div>
              </div>

              <hr class="horizontal dark mt-4 mb-3" />

              <h6 class="text-uppercase text-body text-xs font-weight-bolder">Geolocalización</h6>
              <div class="row">
                <div class="col-md-12 mt-2">
                  <label class="form-label text-sm text-muted">Ajuste el marcador si la ubicación ha cambiado</label>
                  <div id="branch-map" class="border-radius-lg shadow-sm mb-2" style="height: 350px; z-index: 1;"></div>

                  <div class="row">
                    <div class="col-6">
                      <label class="text-xs text-uppercase font-weight-bold">Latitud</label>
                      <argon-input v-model="branch.latitude" readonly size="sm" />
                    </div>
                    <div class="col-6">
                      <label class="text-xs text-uppercase font-weight-bold">Longitud</label>
                      <argon-input v-model="branch.longitude" readonly size="sm" />
                    </div>
                  </div>
                </div>
              </div>

              <div class="d-flex gap-2">
                <argon-button
                    type="button"
                    class="mt-4"
                    color="secondary"
                    variant="outline"
                    fullWidth
                    size="lg"
                    @click="$router.go(-1)"
                >Cancelar</argon-button>

                <argon-button
                    type="submit"
                    class="mt-4"
                    color="success"
                    fullWidth
                    size="lg"
                    :disabled="isLoadingSubmit"
                >
                    <span v-if="isLoadingSubmit">Actualizando...</span>
                    <span v-else>Actualizar Sucursal</span>
                </argon-button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonTextarea from "@/components/ArgonTextarea.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import axios from "axios";
import Swal from 'sweetalert2';
import L from 'leaflet';

export default {
  name: "EditBranch",
  components: {
    ArgonInput,
    ArgonButton,
    ArgonTextarea,
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  data() {
    return {
      map: null,
      marker: null,
      submitted: false,
      isLoadingData: true,
      isLoadingSubmit: false,
      branchId: this.$route.params.id,
      branch: {
        company: null,
        name: null,
        address: null,
        latitude: 0,
        longitude: 0,
        phone: null,
      },
    };
  },
  validations() {
    return {
      branch: {
        name: { required },
        address: { required },
      },
    };
  },
  methods: {
    async fetchBranchData() {
      const token = localStorage.getItem("accessToken");
      const apiURL = this.$store.state.apiURL;

      try {
        const response = await axios.get(`${apiURL}branches/${this.branchId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        this.branch = response.data;

        // 1. Cambiamos el estado para que el DOM se genere (v-else)
        this.isLoadingData = false;

        // 2. Esperamos al siguiente tick de Vue para que el ID 'branch-map' exista físicamente
        this.$nextTick(() => {
            this.initializeMap();
        });

      } catch (err) {
        console.error('Error al cargar sucursal:', err);
        this.isLoadingData = false;
        Swal.fire('Error', 'No se pudo obtener la información de la sucursal.', 'error');
      }
    },

    initializeMap() {
      // Verificación de seguridad: si el elemento no existe, no inicializar
      const container = document.getElementById('branch-map');
      if (!container) return;

      const coords = [parseFloat(this.branch.latitude), parseFloat(this.branch.longitude)];

      // Si el mapa ya estaba inicializado, lo removemos para evitar conflictos
      if (this.map) {
          this.map.remove();
      }

      this.map = L.map('branch-map').setView(coords, 15);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap'
      }).addTo(this.map);

      const icon = L.icon({
        iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
        iconUrl: require('leaflet/dist/images/marker-icon.png'),
        shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
        iconSize: [25, 41],
        iconAnchor: [12, 41]
      });

      this.marker = L.marker(coords, { draggable: true, icon }).addTo(this.map);

      this.marker.on('dragend', () => {
        const position = this.marker.getLatLng();
        this.branch.latitude = position.lat.toFixed(8);
        this.branch.longitude = position.lng.toFixed(8);
      });

      this.map.on('click', (e) => {
        this.marker.setLatLng(e.latlng);
        this.branch.latitude = e.latlng.lat.toFixed(8);
        this.branch.longitude = e.latlng.lng.toFixed(8);
      });

      // Forzar el reajuste del tamaño para evitar que el mapa salga cortado
      setTimeout(() => {
          this.map.invalidateSize();
      }, 200);
    },

    async updateBranch() {
      this.submitted = true;
      this.v$.$touch();

      if (this.v$.$invalid) return;

      this.isLoadingSubmit = true;
      const token = localStorage.getItem("accessToken");
      const apiURL = this.$store.state.apiURL;

      try {
        await axios.put(`${apiURL}branches/${this.branchId}`, this.branch, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        Swal.fire({
          title: 'Actualizado',
          text: 'La sucursal se ha actualizado correctamente.',
          icon: 'success',
          confirmButtonColor: '#2dce89',
        }).then(() => {
          this.$router.push('/branches');
        });
      } catch (err) {
        console.error(err);
        Swal.fire('Error', 'No se pudo actualizar la sucursal.', 'error');
      } finally {
        this.isLoadingSubmit = false;
      }
    }
  },
  mounted() {
    this.fetchBranchData();
  },
  beforeUnmount() {
      // Limpieza del mapa al salir de la vista
      if (this.map) {
          this.map.remove();
      }
  }
};
</script>

<style scoped>
#branch-map {
  border: 1px solid #dee2e6;
  background: #f8f9fa;
  width: 100%;
}
</style>